import React from 'react';
import { GraduationCap, Users, Award, GitBranch, Code, Building2, ShieldCheck, Lock, Target, Briefcase, Bot, Sparkles, Handshake, Workflow, Rocket, Gauge } from 'lucide-react';
interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}
const services: Service[] = [{
  icon: GraduationCap,
  title: 'Career Development Coaching',
  description: 'Personalized guidance to accelerate your career trajectory in technology and leadership.',
  color: 'text-blue-600',
  bgColor: 'bg-blue-50'
}, {
  icon: Users,
  title: 'Corporate Training',
  description: 'Tailored training programs for teams on modern technology practices, security, and architecture.',
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-50'
}, {
  icon: Award,
  title: 'Leadership Development',
  description: 'Servant leadership frameworks and executive coaching for high-tech environments.',
  color: 'text-purple-600',
  bgColor: 'bg-purple-50'
}, {
  icon: GitBranch,
  title: 'End-to-End SDLC Process & Visibility',
  description: 'Complete software development lifecycle optimization with full transparency and governance.',
  color: 'text-cyan-600',
  bgColor: 'bg-cyan-50'
}, {
  icon: Code,
  title: 'Custom Software Development',
  description: 'Bespoke software solutions built with enterprise-grade architecture and security standards.',
  color: 'text-orange-600',
  bgColor: 'bg-orange-50'
}, {
  icon: ShieldCheck,
  title: 'Information Security',
  description: 'Comprehensive security assessments, frameworks, and regulatory-grade compliance strategies.',
  color: 'text-rose-600',
  bgColor: 'bg-rose-50'
}, {
  icon: Building2,
  title: 'Enterprise & Business Architecture',
  description: 'Strategic architecture design for scalable, resilient, and future-proof systems.',
  color: 'text-indigo-600',
  bgColor: 'bg-indigo-50'
}, {
  icon: Lock,
  title: 'Cybersecurity',
  description: 'Advanced threat protection, post-quantum readiness, and security posture assessments.',
  color: 'text-red-600',
  bgColor: 'bg-red-50'
}, {
  icon: Target,
  title: 'Strategic Planning',
  description: 'Technology roadmaps and strategic initiatives aligned with business objectives.',
  color: 'text-amber-600',
  bgColor: 'bg-amber-50'
}, {
  icon: Briefcase,
  title: 'Business Consulting',
  description: 'Expert advisory on digital transformation, market positioning, and operational efficiency.',
  color: 'text-teal-600',
  bgColor: 'bg-teal-50'
}, {
  icon: Bot,
  title: 'AI Product Creation',
  description: 'End-to-end AI product development from ideation to deployment with ethical AI practices.',
  color: 'text-violet-600',
  bgColor: 'bg-violet-50'
}, {
  icon: Sparkles,
  title: 'AI Utilization for Efficiency',
  description: 'Leverage AI to streamline operations, automate workflows, and drive measurable productivity gains.',
  color: 'text-fuchsia-600',
  bgColor: 'bg-fuchsia-50'
}, {
  icon: Workflow,
  title: 'AI-DLC',
  description: 'Spec-driven AI development lifecycle design — governance, traceability, and quality gates for agent-assisted delivery.',
  color: 'text-sky-600',
  bgColor: 'bg-sky-50'
}, {
  icon: Rocket,
  title: 'AI Transformation',
  description: 'Enterprise-wide roadmaps to embed AI across products, engineering, and operations without disrupting delivery.',
  color: 'text-lime-600',
  bgColor: 'bg-lime-50'
}, {
  icon: Gauge,
  title: 'Company AI Readiness',
  description: 'Assess data, security, talent, and process maturity to establish a clear, prioritized path to AI adoption.',
  color: 'text-pink-600',
  bgColor: 'bg-pink-50'
}];
export function ConsultingSection() {
  return <section id="consulting" className="py-24 bg-[#0B1120] px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-83" data-mp="cq4g8ssrg8be">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-84" data-mp="3qhe24s7iyha">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl opacity-40" data-id="element-85" data-mp="3s0vhyplnfkn"></div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl opacity-40" data-id="element-86" data-mp="7tmbvu1cebzl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative" data-id="element-87" data-mp="10h04xh1x6nef">
        <div className="text-center mb-16" data-id="element-88" data-mp="10ikwcxsxtq82">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/20 rounded-2xl mb-4 border border-cyan-500/30 glow-cyan backdrop-blur-sm" data-id="element-89" data-mp="mpljl3kjia47">
            <Handshake className="h-8 w-8 text-cyan-400" data-id="element-90" data-mp="qoarigmt6zgg" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-91" data-mp="1eeopsovzlujh">
            Consulting Services
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto rounded-full mb-6" data-id="element-92" data-mp="1j92j8lr39rli"></div>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-93" data-mp="2yya987bpya7j">
            Leveraging decades of experience across defense, fintech, and
            enterprise systems to help organizations build, scale, and secure
            their technology.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-94" data-mp="38ovdaaa5w6xu">
          {services.map((service, index) => <div key={index} className="group bg-white/5 backdrop-blur-sm rounded-xl p-6 shadow-sm hover:shadow-lg border border-white/10 hover:border-cyan-500/30 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1 flex flex-col" data-id="element-95" data-mp="2fzxy4g18yq9b" data-mpi={index}>
              <div className={`inline-flex items-center justify-center w-12 h-12 bg-white/5 border border-white/10 rounded-xl mb-4 group-hover:scale-110 transition-transform`} data-id="element-96" data-mp="a67pf843euj4">
                <service.icon className={`h-6 w-6 ${service.color.replace('600', '400')}`} data-id="element-97" data-mp="cb2ydn8vd29s" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors" data-id="element-98" data-mp="h838c98uap66">
                {service.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed flex-grow" data-id="element-99" data-mp="r83styig3bby">
                {service.description}
              </p>
            </div>)}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center" data-id="element-100" data-mp="3uo11g30ykb86">
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-cyan-500/20 shadow-sm glow-cyan" data-id="element-101" data-mp="3uou1ym9bq4hv">
            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-102" data-mp="8a199z8o26lj">
              Ready to Transform Your Organization?
            </h3>
            <p className="text-slate-300 max-w-xl mx-auto mb-8" data-id="element-103" data-mp="36yq28pz9aow5">
              Whether you need strategic guidance, hands-on technical
              leadership, or AI-driven innovation, let's discuss how to move
              your business forward.
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-colors shadow-lg" data-id="element-104" data-mp="1p2eif0ky60vs">
              Get Started
              <Sparkles className="h-4 w-4" data-id="element-105" data-mp="1f5jl2l0eu6j3" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}